import { CrudComponent } from "./CrudComponent";
import { CrudDialog } from "./CrudDialog";
import { CrudEditForm } from "./CrudEditForm";
import { CrudShow } from "./CrudShow";

export interface CrudEditDialogProps<Row = any> {
  open: boolean;
  id?: string;
  onClose: () => void;
  onClickTab: (tab: string) => void;
  editSchema: any;
  texts: {
    editItem: string;
    itemUpdated?: string;
    unsavedChangesWarning?: string;
  };
  tab: string;
  refetchQueries?: string[];
  customHeaderButton?: React.ReactNode;
  onSubmitSuccess?: (data: Row) => void;
}

export function CrudEditDialog<Row = any>({
  open,
  id,
  onClose,
  onClickTab,
  editSchema,
  texts,
  tab,
  refetchQueries = [],
  customHeaderButton,
  onSubmitSuccess,
}: CrudEditDialogProps<Row>) {
  if (!editSchema[tab]) return null;

  const steps = Object.keys(editSchema)
    .filter((key) => {
      if (editSchema[key].visible) {
        return editSchema[key].visible({ tab });
      }
      return true;
    })
    .map((key) => ({
      label: editSchema[key].label,
      selected: tab === key,
      onClick: () => onClickTab(key),
    }));

  const title = texts.editItem;

  const {
    type,
    label,
    schema,
    updateMutation,
    editQuery,
    getEditData,
    showQuery,
    showComponent,
    showQueryVariables,
    showPollInterval,
    showSkipPollAttempt,
    getConfirm,
    getTitle,
    component: Component,
    componentQuery,
    warnOnUnsavedChanges,
    unsavedChangesWarningMessage,
    paper,
  } = editSchema[tab];

  return (
    <CrudDialog
      open={open}
      onClose={onClose}
      steps={steps}
      title={title}
      subTitle={label}
    >
      {type === "component" && id && (
        <CrudComponent
          id={id}
          key={tab + "component"}
          componentQuery={componentQuery}
          component={Component}
          title={title}
          subTitle={label}
          onClose={onClose}
          getTitle={getTitle}
        />
      )}

      {type === "show" && id && (
        <CrudShow
          id={id}
          key={tab + "show"}
          showQuery={showQuery}
          showPollInterval={showPollInterval}
          showSkipPollAttempt={showSkipPollAttempt}
          showComponent={showComponent}
          showQueryVariables={showQueryVariables}
          title={title}
          subTitle={label}
          onClose={onClose}
          getTitle={getTitle}
        />
      )}

      {type === "form" && id && (
        <CrudEditForm
          key={tab + "form"}
          schema={schema}
          updateMutation={updateMutation}
          editQuery={editQuery}
          getEditData={getEditData}
          id={id}
          texts={{
            itemUpdated: texts.itemUpdated || texts.editItem,
            unsavedChangesWarning: texts.unsavedChangesWarning,
          }}
          refetchQueries={refetchQueries}
          title={title}
          subTitle={label}
          onClose={onClose}
          getConfirm={getConfirm}
          getTitle={getTitle}
          customHeaderButton={customHeaderButton}
          onSubmitSuccess={onSubmitSuccess}
          warnOnUnsavedChanges={warnOnUnsavedChanges}
          unsavedChangesWarningMessage={unsavedChangesWarningMessage}
          paper={paper}
        />
      )}
    </CrudDialog>
  );
}
